import {
	type ActionShortcutOption,
	type ResourcePlanningGantt as Gantt,
	type ResourcePlanningItem,
	type ResourcePlanningComponent,
	type ZoomScaleConfig,
	type HeatMapOption,
	type BookingTypeFilter,
	type ResourcingUserUtilization,
	type BookingsHeatMapData,
	type ExternalRendererPayload,
} from '../../types';
import {
	ACTION_SHORTCUT_OPTIONS,
	CHART_ITEM_TYPE,
	RENDER_TYPE_SPLIT,
	RESOURCE_PROPERTY,
	SETTINGS,
	ZOOM_OPTIONS,
	ZOOM_SCALE_OPTIONS,
	ZOOM_UNIT,
	HEAT_MAP_OPTIONS,
	BOOKING_TYPE_FILTER,
	PLACEHOLDER_BG_COLOR,
	CHART_ROW_TYPE,
	RESOURCE_PLANNING_CONTAINER_ID,
	RESOURCE_PLANNING_PORTFOLIO_CONTAINER_ID,
} from '../../constants';
import {
	renderComponent,
	getChartResourceId,
	getResourceId,
	getResourcePlanningSetting,
	openBookingModal,
	getTotalBookingDuration,
	areBookingDatesOverlapping,
	weekStartsOnSettingDateFnsMapping,
	getZoomToFitZoomLevel,
	convertHexToHSL,
	getIconHTML,
	getResourceType,
} from './ResourcePlanningGanttUtils';
import { type ResourcePlanningActionShortcutProps } from '../ResourcePlanning/ResourcePlanningActionShortcut.vue';
import { type ResourcePlanningBookingModalProps } from '../ResourcePlanningBookingModal';
import { useFormatter, usePermissions, useTranslation } from '@/composables';
import { type LiterallyAnything } from '@/types/utils';
import { resetTime, shortDuration } from '@/utils/formatting';
import {
	type Booking,
	BookingAmountType,
	type ResourcePlanningQuery,
	type UserUtilizationResponse,
	type BookingSaveInput,
	type User,
	type RoleProject,
	TYPES,
	type Role,
	type Project,
	type TimeOff,
	type ResourcePlanningTimeOffQuery,
	type RetainerPeriod,
} from '@/generated/graphql';
import { isTruthy } from '@/utils/filters';
import { sanitizeHTML, getUniqueId } from '@/utils/misc';
import {
	addDays,
	eachWeekOfInterval,
	eachMonthOfInterval,
	addMonths,
	startOfMonth,
	eachDayOfInterval,
	min,
	max,
	isBefore,
	isAfter,
	isWithinInterval,
	startOfWeek,
	endOfWeek,
} from 'date-fns';
import { Setting, WEEK_STARTS_ON } from '@/constants/settings';
import { type GanttChartColumn } from '@/types/misc';
import { useAvailabilityService } from '@/features/Availability';
import { isWeekend } from '@/utils/time';
import { memoize } from '@/utils/memoize';
import { dateToMysqlString } from '@/composables/settings/useFormatter/formatterUtils';
import { type SAPIDate } from '@/types/sapi/scalars';
import { computed } from 'vue';
import { Constants } from '@/constants';
import {
	useBooleanSetting,
	useSettings,
} from '@/composables/settings/useSettings';
import { TimeOffSystemType } from '@/features/Calendar/components/TimeOff';
export class ResourcePlanningGanttConfig {
	gantt: Gantt;

	chartProjectId = '';

	retainerPeriodId = '';

	projectManagerId = '';

	projectStartDate = '';

	projectColor = '';

	projectPlannedStartDate = new Date();

	projectPlannedEndDate: Date | null = null;

	projectMemberIds: string[] = [];

	projectRetainerPeriods: Pick<
		RetainerPeriod,
		'id' | 'startDate' | 'endDate'
	>[] = [];

	heatMapDisplayValue: HeatMapOption = HEAT_MAP_OPTIONS.PERCENT;

	showPlaceholders = true;

	hideEmptyProjectsValue = false;

	cachedHeatMapData: Record<
		string,
		Record<string, Record<string, BookingsHeatMapData>>
	> = {};

	cachedHeatMapElements: Map<string, HTMLElement> = new Map();

	bookingTypeFilterValue: BookingTypeFilter =
		BOOKING_TYPE_FILTER.FIXED_AND_TENTATIVE;

	weekStartsOn = computed(
		() =>
			weekStartsOnSettingDateFnsMapping[
				Number(
					this.settings.setting(Constants.Setting.WEEK_STARTS_ON) ??
						WEEK_STARTS_ON.MONDAY
				)
			]
	);

	utilizationData: {
		[TYPES.User]: {
			[key: User['id']]: {
				[key: string]: ResourcingUserUtilization;
			};
		};
		[TYPES.Role]: {
			[key: Role['id']]: {
				[key: string]: ResourcingUserUtilization;
			};
		};
	} = {
		User: {},
		Role: {},
	};

	timeOffData: {
		[key: User['id']]: {
			[key: SAPIDate]: TimeOff;
		};
	} = {};

	placeholderId = 'add-member-placeholder' as const;

	formatter: ReturnType<typeof useFormatter>;

	t;

	availabilityService: ReturnType<typeof useAvailabilityService>;

	permissions: ReturnType<typeof usePermissions>;

	settings: ReturnType<typeof useSettings>;

	defaultZoomLevel: number | undefined = undefined;

	onProjectUserRemoved?: (id: User['id'], projectId?: Project['id']) => void;

	onProjectRoleRemoved?: (
		id: RoleProject['id'],
		projectId?: Project['id']
	) => void;

	onMoveBookings?: (
		toUserId: User['id'],
		fromUserId?: User['id'],
		fromRoleProjectId?: RoleProject['id'],
		projectId?: Project['id']
	) => void;

	onUpdateBooking?: (
		input: BookingSaveInput,
		entityId: User['id'] | RoleProject['id'],
		entityType: typeof TYPES.RoleProject | typeof TYPES.User
	) => void;

	onClearBookings?: (
		id: User['id'] | RoleProject['id'],
		entityType: typeof TYPES.RoleProject | typeof TYPES.User,
		projectId: Project['id']
	) => void;

	onAssignBookings?: (
		toUserId: User['id'],
		fromRoleProjectId: RoleProject['id'],
		projectId: Project['id']
	) => void;

	shouldAddToUndoStack = false;

	containerElem: HTMLElement | undefined;

	isReadOnly = true;

	isSingleProject = false;

	hasTentativeBookingsFeature = computed<boolean>(() =>
		this.permissions.userHas(Constants.Feature.TENTATIVE_BOOKINGS)
	);

	headerHeight = 34;

	rowHeight = 41;

	barHeight = 32;

	minDuration = 1440; // minutes (1 day);

	dateFormatDB = '%Y-%m-%d';

	dragInProgress = false;

	includeWeekendsWhenScheduling = useBooleanSetting(
		Setting.INCLUDE_WEEKENDS_WHEN_SCHEDULING
	)[0];

	plugins: Record<string, boolean> = {
		marker: true,
		auto_scheduling: true,
		keyboard_navigation: true,
		click_drag: true,
		tooltip: true,
		drag_timeline: true,
		grouping: true,
	};

	layout = {
		css: 'gantt_container',
		cols: [
			{
				width: 400,
				min_width: 300,
				rows: [
					{
						view: 'grid',
						scrollX: 'gridScroll',
						scrollable: true,
						scrollY: 'scrollVer',
					},
					{ view: 'scrollbar', id: 'gridScroll' },
				],
			},
			{ resizer: true, width: 1 },
			{
				rows: [
					{
						view: 'timeline',
						scrollX: 'scrollHor',
						scrollY: 'scrollVer',
					},
					{ view: 'scrollbar', id: 'scrollHor' },
				],
			},
			{ view: 'scrollbar', id: 'scrollVer' },
		],
	};

	editors = {
		text: {
			type: 'text',
			map_to: 'text',
		},
		dummy: {
			type: 'dummy',
			map_to: 'dummy',
		},
	};

	canModifyProject(_item: ResourcePlanningItem | undefined): boolean {
		return !this.isReadOnly;
	}

	showRemoveProjectMember(item: ResourcePlanningItem) {
		return (
			getResourceType(item.id) !== TYPES.User ||
			getResourceId(item.id) !== this.projectManagerId
		);
	}

	getColumnConfig = (): GanttChartColumn[] => [
		{
			name: 'text',
			label: this._getNameColumnLabel(),
			tree: true,
			width: 200,
			min_width: 200,
			resize: true,
			editor: this.editors.text,
			template: (item: ResourcePlanningItem) =>
				this._getNameColumnTemplate(item),
		},
		{
			name: 'booked_duration',
			label: this._getBookedDurationColumnLabel(),
			align: 'right',
			resize: true,
			min_width: 40,
			template: (item: ResourcePlanningItem) =>
				this._getBookedDurationColumnTemplate(item),
		},
		{
			name: 'actions',
			label: '',
			width: 32,
			align: 'center',
			template: () => this._getActionsColumnTemplate(),
			onrender: (item: ResourcePlanningItem) => {
				if (
					!this.canModifyProject(item) ||
					this.dragInProgress ||
					item.row_type !== CHART_ROW_TYPE.BOOKINGS
				) {
					return;
				}

				const options: ActionShortcutOption[] = [
					ACTION_SHORTCUT_OPTIONS.NEW_BOOKING,
				];

				if (this.showRemoveProjectMember(item)) {
					options.push(ACTION_SHORTCUT_OPTIONS.REMOVE_PROJECT_MEMBER);
				}

				if (this._hasBookings(item)) {
					options.push(
						ACTION_SHORTCUT_OPTIONS.MOVE_BOOKINGS,
						ACTION_SHORTCUT_OPTIONS.CLEAR_BOOKINGS
					);
				}

				if (
					getResourceType(item.id) === TYPES.RoleProject ||
					item.role_project_id
				) {
					options.push(ACTION_SHORTCUT_OPTIONS.ASSIGN_MEMBER);
				}

				const projectId = this.getQuickActionProjectId(item);
				const entityId = this.getQuickActionEntityId(item);
				const entityType = this.getQuickActionEntityType(item);
				const project = this.getQuickActionTargetProject(item);
				const resource = this.getQuickActionTargetResource(item);

				const props: ResourcePlanningActionShortcutProps & {
					onProjectMemberRemoved: (id: User['id']) => void;
					onProjectRoleRemoved: (id: RoleProject['id']) => void;
					onMoveBookings: (
						toUserId: User['id'],
						fromEntityId: User['id'] | RoleProject['id'],
						fromEntityType:
							| typeof TYPES.RoleProject
							| typeof TYPES.User,
						projectId: Project['id']
					) => void;
					onClearBookings: (
						id: User['id'] | RoleProject['id'],
						entityType:
							| typeof TYPES.RoleProject
							| typeof TYPES.User,
						projectId: Project['id']
					) => void;
					onUpdateBooking: (
						input: BookingSaveInput,
						entityId: User['id'] | RoleProject['id'],
						entityType: typeof TYPES.RoleProject | typeof TYPES.User
					) => void;
					onAssignBookings: (
						toUserId: User['id'],
						fromRoleProjectId: RoleProject['id'],
						projectId: Project['id']
					) => void;
				} = {
					options,
					projectId,
					retainerPeriodId: this.retainerPeriodId,
					projectRetainerPeriods: this.projectRetainerPeriods,
					projectStartDate: this.isSingleProject
						? resetTime(this.projectStartDate)
						: resetTime(new Date()),
					entityId,
					entityType,
					hasTentativeBookingsFeature:
						this.hasTentativeBookingsFeature.value,
					config: this,
					resource,
					project,
					onProjectMemberRemoved: (
						id: User['id'],
						projectId?: Project['id']
					) => {
						this.onProjectUserRemoved?.(id, projectId);
					},
					onProjectRoleRemoved: (
						id: RoleProject['id'],
						projectId?: Project['id']
					) => {
						this.onProjectRoleRemoved?.(id, projectId);
					},
					onMoveBookings: (
						toUserId: User['id'],
						fromEntityId: User['id'] | RoleProject['id'],
						fromEntityType:
							| typeof TYPES.RoleProject
							| typeof TYPES.User,
						projectId: Project['id']
					) => {
						this.onMoveBookings?.(
							toUserId,
							fromEntityType === TYPES.User
								? fromEntityId
								: undefined,
							fromEntityType === TYPES.RoleProject
								? fromEntityId
								: undefined,
							projectId
						);
					},
					onClearBookings: (
						id: User['id'] | RoleProject['id'],
						entityType: typeof TYPES.RoleProject | typeof TYPES.User
					) => {
						this.onClearBookings?.(id, entityType, projectId);
					},
					onUpdateBooking: (
						input: BookingSaveInput,
						entityId: User['id'] | RoleProject['id'],
						entityType: typeof TYPES.RoleProject | typeof TYPES.User
					) => {
						this.onUpdateBooking?.(input, entityId, entityType);
					},
					onAssignBookings: (
						toUserId: User['id'],
						fromRoleProjectId: RoleProject['id'],
						projectId: Project['id']
					) => {
						this.onAssignBookings?.(
							toUserId,
							fromRoleProjectId,
							projectId
						);
					},
				};

				return {
					component: 'ResourcePlanningActionShortcut',
					props,
				};
			},
			editor: this.editors.dummy,
		},
	];

	zoomConfig = {
		levels: this.getAllScaleConfigLevels(),
		useKey: 'ctrlKey',
		trigger: 'wheel',
		element: () => {
			return this.containerElem?.querySelector('.gantt_task');
		},
	};

	stickyDate: HTMLDivElement = Object.assign(document.createElement('div'), {
		id: 'bookings-sticky-date',
	});

	constructor(gantt: Gantt) {
		this.gantt = gantt;
		this.formatter = useFormatter();
		const { t } = useTranslation();
		this.t = t;
		this.availabilityService = useAvailabilityService();
		this.permissions = usePermissions();
		this.settings = useSettings();

		this._getDaysWithinRange = memoize(this._getDaysWithinRange);
	}

	applyConfig() {
		this.gantt.plugins(this.plugins);
		this.gantt.ext.zoom.init(this.zoomConfig);

		this.gantt.config.grid_elastic_columns = true;
		this.gantt.config.layout = this.layout;

		this.gantt.config.min_column_width = 60;
		this.gantt.config.fit_tasks = true;
		this.gantt.config.drag_project = false;
		this.gantt.config.work_time = false;
		this.gantt.config.show_links = false;
		this.gantt.config.time_step = this.minDuration;

		this.gantt.config.auto_scheduling_move_projects = false;
		this.gantt.config.round_dnd_dates = false;
		this.gantt.config.duration_unit = 'day';
		this.gantt.config.duration_step = 1;
		this.gantt.config.row_height = this.rowHeight;
		this.gantt.config.bar_height = this.barHeight;
		this.gantt.config.date_format = this.dateFormatDB;
		this.gantt.config.task_date = this.dateFormatDB;
		this.gantt.config.keyboard_navigation_cells = true;
		this.gantt.config.tooltip_timeout = 300;
		this.gantt.config.font_width_ratio = 7;
		this.gantt.config.show_progress = false;
		this.gantt.config.min_duration = this.minDuration;
		this.gantt.config.scale_height = this.rowHeight;
		this.gantt.config.scroll_on_click = false;
		this.gantt.config.static_background = true;
		this.gantt.config.select_task = false;
		this.gantt.config.resource_property = RESOURCE_PROPERTY;
		this.gantt.config.drag_timeline = {
			ignore: '.gantt_task_line, .gantt_task_link',
			useKey: false,
		};

		this.gantt.templates.tooltip_text = (
			_start,
			_end,
			item: ResourcePlanningItem
		) => this._getTooltipText(item);
		this.gantt.templates.grid_open = (item) =>
			this._getGridOpenTemplate(item);
		this.gantt.templates.grid_folder = (item) =>
			this._getGridFolderTemplate(item);
		this.gantt.templates.grid_file = (item) =>
			this._getGridFolderTemplate(item);
		this.gantt.templates.task_class = (start, end, item) =>
			this._getTaskClass(item);
		this.gantt.templates.grid_row_class = (start, end, item) =>
			this._getGridRowClass(item);
		this.gantt.templates.task_row_class = (start, end, item) =>
			this._getTaskRowClass(item);
		this.gantt.templates.task_text = (start, end, item) =>
			this._getTaskText(item);
		this.gantt.templates.rightside_text = (start, end, task) =>
			this._getTaskTextByPosition(task, 'right');
		this.gantt.templates.leftside_text = (start, end, task) =>
			this._getTaskTextByPosition(task, 'left');
		this.gantt.templates.scale_cell_class = this._getScaleCellClass;
		this.gantt.showLightbox = () => {};

		this.gantt.i18n.setLocale({
			date: {
				month_full: [
					this.t('month_1'),
					this.t('month_2'),
					this.t('month_3'),
					this.t('month_4'),
					this.t('month_5'),
					this.t('month_6'),
					this.t('month_7'),
					this.t('month_8'),
					this.t('month_9'),
					this.t('month_10'),
					this.t('month_11'),
					this.t('month_12'),
				],
				month_short: [
					this.t('month_short_1'),
					this.t('month_short_2'),
					this.t('month_short_3'),
					this.t('month_short_4'),
					this.t('month_short_5'),
					this.t('month_short_6'),
					this.t('month_short_7'),
					this.t('month_short_8'),
					this.t('month_short_9'),
					this.t('month_short_10'),
					this.t('month_short_11'),
					this.t('month_short_12'),
				],
				day_full: [
					this.t('wday_7'),
					this.t('wday_1'),
					this.t('wday_2'),
					this.t('wday_3'),
					this.t('wday_4'),
					this.t('wday_5'),
					this.t('wday_6'),
				],
				day_short: [
					this.t('wday_short_7'),
					this.t('wday_short_1'),
					this.t('wday_short_2'),
					this.t('wday_short_3'),
					this.t('wday_short_4'),
					this.t('wday_short_5'),
					this.t('wday_short_6'),
				],
			},
		});
	}

	setIsReadOnly(isReadOnly: boolean) {
		this.isReadOnly = isReadOnly;
	}

	setIsSingleProject(isSingleProject: boolean) {
		this.isSingleProject = isSingleProject;
	}

	setChartProjectId(id: string) {
		this.chartProjectId = id;
	}

	setRetainerPeriodId(id: string) {
		this.retainerPeriodId = id;
	}

	setProjectManagerId(id: string) {
		this.projectManagerId = id;
	}

	setProjectStartDate(date: string) {
		this.projectStartDate = date;
	}

	addProjectDatesLayer(
		item: ResourcePlanningItem,
		container: HTMLDivElement
	) {
		if (item?.item_type === CHART_ITEM_TYPE.PROJECT) {
			const startDate = this.getProjectPlannedStartDate(item);
			const endDate = this.getProjectPlannedEndDate(item);
			const taskDimensions = this.gantt.getTaskPosition(
				item,
				startDate,
				endDate ?? startDate
			);

			const startPosition = this.gantt.posFromDate(startDate);

			const topPosition =
				taskDimensions.top + taskDimensions.rowHeight / 2;
			const elementColor = convertHexToHSL(
				this.getProjectColor(item),
				38
			);
			const tooltipTimeout = String(this.gantt.config.tooltip_timeout);

			if (startPosition > 0) {
				const startElem = document.createElement('div');

				startElem.innerHTML = getIconHTML(
					'icon-play-filled-24',
					elementColor
				);
				startElem.className = 'resource-planning__project-date-block';
				startElem.style.top = topPosition + 'px';
				startElem.style.left = startPosition + 'px';
				startElem.setAttribute('data-tipsy-delay-in', tooltipTimeout);
				startElem.setAttribute('data-respect-html', 'true');
				startElem.title = `${this.t(
					'estimated_project_start_date'
				)}<br>${this.formatter.formatDate(startDate)}`;

				container.appendChild(startElem);
			}

			if (endDate) {
				const endElem = document.createElement('div');
				const endElemIcon = getIconHTML(
					'icon-diamond-filled-24',
					elementColor
				);
				const endPosition = this.gantt.posFromDate(addDays(endDate, 1));

				if (endPosition < this.gantt.$task_data.clientWidth) {
					endElem.innerHTML = endElemIcon;
					endElem.className = 'resource-planning__project-date-block';
					endElem.style.left = endPosition + 'px';
					endElem.style.top = topPosition + 'px';
					endElem.setAttribute('data-tipsy-delay-in', tooltipTimeout);
					endElem.setAttribute('data-respect-html', 'true');
					endElem.title = `${this.t(
						'estimated_project_due_date'
					)}<br>${this.formatter.formatDate(endDate)}`;

					container.appendChild(endElem);
				}
			}
		}
	}

	addResourcePlanningToolLayers() {
		this.gantt.addTaskLayer({
			renderer: {
				render: (item: ResourcePlanningItem) => {
					const container = document.createElement('div');

					this.addProjectDatesLayer(item, container);

					this.addHeatMapLayer(item, container);

					return container;
				},
				// This renders only the layers that are inside the chart vertical viewport
				getRectangle: (item: ResourcePlanningItem) => {
					return this.gantt.getTaskPosition(
						item,
						this.gantt.config.start_date,
						this.gantt.config.end_date
					);
				},
			},
		});
	}

	setHeatMapDisplayValue(value: HeatMapOption) {
		this.heatMapDisplayValue = value;
	}

	setBookingTypeFilterValue(value: BookingTypeFilter) {
		this.bookingTypeFilterValue = value;
	}

	setUtilizationData(
		type: typeof TYPES.User | typeof TYPES.Role,
		userOrRoleId: User['id'] | Role['id'],
		data: UserUtilizationResponse['utilizations']
	) {
		for (let i = 0; i < data.length; i++) {
			const utilizationData = data[i];

			if (!this.utilizationData[type][userOrRoleId]) {
				this.utilizationData[type][userOrRoleId] = {};
			}

			if (utilizationData.date) {
				this.utilizationData[type][userOrRoleId][utilizationData.date] =
					{
						...utilizationData,
						date: resetTime(utilizationData.date),
					};
			}
		}
	}

	setTimeOffData(
		data: ResourcePlanningTimeOffQuery['UserTimeOffList'][number]
	) {
		if (!this.timeOffData[data.user.id]) {
			this.timeOffData[data.user.id] = {};
		}

		this.timeOffData[data.user.id][data.date] = { ...data.timeOff };
	}

	deleteTimeOffData(userIds: string[]) {
		userIds.forEach((userId) => {
			delete this.timeOffData[userId];
		});
	}

	appendElements(cells: Array<HTMLElement>, container: HTMLDivElement) {
		for (let i = 0; i < cells.length; i++) {
			const elem = cells[i];
			if (elem) {
				container.appendChild(elem);
			}
		}
	}

	isUtilizationLoaded(): boolean {
		return (
			Object.values(this.utilizationData.User).length > 0 ||
			Object.values(this.utilizationData.Role).length > 0
		);
	}

	_getResourceUtilization(item: ResourcePlanningItem): {
		[key: string]: ResourcingUserUtilization;
	} {
		if (!this.isUtilizationLoaded()) return {};

		const getUtilization = (type: 'User' | 'Role', id: string) =>
			this.utilizationData[type]?.[id] ?? {};

		const resourceUtilization: {
			[key: string]: ResourcingUserUtilization;
		} = {};

		switch (item.item_type) {
			case CHART_ITEM_TYPE.ROLE:
				if (item.role_id)
					return getUtilization(TYPES.Role, item.role_id);
				break;

			case CHART_ITEM_TYPE.RESOURCE:
				return getUtilization(TYPES.User, getResourceId(item.id));

			case CHART_ITEM_TYPE.PROJECT:
			case CHART_ITEM_TYPE.ADD_TO_PROJECT:
				if (getResourceType(item.resource_id) === TYPES.Role) {
					return getUtilization(
						TYPES.Role,
						getResourceId(item.resource_id)
					);
				} else if (item.parent) {
					return getUtilization(
						TYPES.User,
						getResourceId(item.parent)
					);
				}
				break;

			case CHART_ITEM_TYPE.BOOKING_LIST:
				if (item.role_id)
					return getUtilization(TYPES.Role, item.role_id);
				if (item.user_id)
					return getUtilization(TYPES.User, item.user_id);
				break;

			case CHART_ITEM_TYPE.ROLE_DIRECTORY:
				this.gantt.eachTask((resource) => {
					const utilization = this._getResourceUtilization(resource);

					for (const [date, value] of Object.entries(utilization)) {
						if (!resourceUtilization[date]) {
							resourceUtilization[date] = {
								...value,
								tentativeBookedDuration: 0,
								fixedBookedDuration: 0,
								availability: 0,
							};
						}

						if (
							resource.item_type === CHART_ITEM_TYPE.RESOURCE ||
							resource.item_type === CHART_ITEM_TYPE.ROLE
						) {
							resourceUtilization[date].tentativeBookedDuration +=
								value.tentativeBookedDuration;
							resourceUtilization[date].fixedBookedDuration +=
								value.fixedBookedDuration;
							resourceUtilization[date].availability +=
								resource.item_type === CHART_ITEM_TYPE.RESOURCE
									? value.availability
									: 0;
						}
					}
				}, item.id);
				break;
		}

		return { ...resourceUtilization };
	}

	_getHeatMapElemList(item: ResourcePlanningItem): HTMLElement[] {
		const userUtilizationData: {
			[key: string]: ResourcingUserUtilization;
		} = this._getResourceUtilization(item);
		let heatMapElemList: Array<HTMLElement | undefined> = [];
		let timeOffData: { [key: SAPIDate]: TimeOff } = {};
		let timeOffElemList: Array<HTMLElement | undefined> = [];

		if (item.item_type !== CHART_ITEM_TYPE.ROLE) {
			timeOffData = this.timeOffData[getResourceId(item.id)];
		}
		const hasUtilizationData =
			Object.values(userUtilizationData).length > 0;
		const hasTimeOffData =
			item.item_type !== CHART_ITEM_TYPE.ROLE && timeOffData;

		if (hasUtilizationData || hasTimeOffData) {
			const selectedZoomOption =
				ZOOM_SCALE_OPTIONS[this.gantt.ext.zoom.getCurrentLevel()];
			const { min_date: ganttMin, max_date: ganttMax } =
				this.gantt.getState();

			const firstDateInInterval = resetTime(ganttMin);
			const lastDateInInterval = resetTime(ganttMax);

			switch (selectedZoomOption) {
				case ZOOM_OPTIONS.MONTH:
					const monthsInInterval = eachMonthOfInterval({
						start: firstDateInInterval,
						end: lastDateInInterval,
					});

					monthsInInterval.map((date) => {
						if (hasUtilizationData) {
							heatMapElemList.push(
								this._makeMonthElement(
									item,
									date,
									Object.values(userUtilizationData)
								)
							);
						}

						if (hasTimeOffData) {
							timeOffElemList.push(
								this._getTimeOffRangeElem({
									item,
									date,
									timeOffData,
									duration: 'month',
									utilization: userUtilizationData,
								})
							);
						}
					});

					break;
				case ZOOM_OPTIONS.WEEK:
					const { min_date: start, max_date: end } =
						this.gantt.getState();
					const weeksInInterval = eachWeekOfInterval(
						{
							start,
							end,
						},
						// TODO: currently the gantt chart is not configured to custom day start
						// so in gantt week scale the first day always is Monday
						{
							weekStartsOn:
								weekStartsOnSettingDateFnsMapping[
									WEEK_STARTS_ON.MONDAY
								],
						}
					);

					weeksInInterval.map((date) => {
						if (hasUtilizationData) {
							heatMapElemList.push(
								this._makeWeekElement(
									item,
									date,
									userUtilizationData
								)
							);
						}

						if (hasTimeOffData) {
							timeOffElemList.push(
								this._getTimeOffRangeElem({
									item,
									date,
									timeOffData,
									duration: 'week',
									utilization: userUtilizationData,
								})
							);
						}
					});
					break;
				case ZOOM_OPTIONS.DAY:
					if (hasUtilizationData) {
						const { min_date: start, max_date: end } =
							this.gantt.getState();
						const dates = this._getDaysWithinRange(start, end);

						heatMapElemList = dates.map((date) =>
							this._getHeatMapDayElem(
								item,
								userUtilizationData[date]
							)
						);
					}

					if (hasTimeOffData) {
						timeOffElemList = Object.keys(timeOffData).map((date) =>
							this._getTimeOffDayElem({
								item,
								date,
								timeOff: timeOffData[date],
								utilization: userUtilizationData,
							})
						);
					}
					break;
			}
		}

		return heatMapElemList.concat(timeOffElemList).filter(isTruthy);
	}

	addHeatMapLayer(item: ResourcePlanningItem, node: HTMLDivElement) {
		if (
			(item.row_type !== CHART_ROW_TYPE.HEATMAP &&
				item.item_type !== CHART_ITEM_TYPE.PROJECT &&
				item.item_type !== CHART_ITEM_TYPE.ADD_TO_PROJECT &&
				item.item_type !== CHART_ITEM_TYPE.BOOKING_LIST) ||
			!item.start_date ||
			!item.end_date
		) {
			return;
		}

		const taskDimensions = this.gantt.getTaskPosition(
			item,
			item.start_date,
			item.end_date
		);
		const heatMapContainer = document.createElement('div');
		heatMapContainer.className = 'resource-planning__heat-map-layer';
		heatMapContainer.style.top = `${taskDimensions.top}px`;
		node.append(heatMapContainer);

		const heatMapElemList = this._getHeatMapElemList(item);

		if (heatMapElemList.length > 0) {
			this.appendElements(heatMapElemList, heatMapContainer);
		}
	}

	setGantt(gantt: Gantt) {
		this.gantt = gantt;
	}

	setContainerElem(containerElem: HTMLElement) {
		this.containerElem = containerElem;
	}

	setOnProjectUserRemoved(
		callback: (id: User['id'], projectId?: Project['id']) => void
	) {
		this.onProjectUserRemoved = callback;
	}

	setOnProjectRoleRemoved(
		callback: (id: RoleProject['id'], projectId?: Project['id']) => void
	) {
		this.onProjectRoleRemoved = callback;
	}

	setOnMoveBookings(
		callback: (
			toUserId: User['id'],
			fromUserId?: User['id'],
			fromRoleProjectId?: RoleProject['id'],
			projectId?: Project['id']
		) => void
	) {
		this.onMoveBookings = callback;
	}

	setOnAssignBookings(
		callback: (
			toUserId: User['id'],
			fromRoleProjectId: RoleProject['id'],
			projectId?: Project['id']
		) => void
	) {
		this.onAssignBookings = callback;
	}

	setOnUpdateBooking(
		callback: (
			input: BookingSaveInput,
			entityId: User['id'] | RoleProject['id'],
			entityType: typeof TYPES.RoleProject | typeof TYPES.User
		) => void
	) {
		this.onUpdateBooking = callback;
	}

	setOnClearBookings(
		callback: (
			id: User['id'] | RoleProject['id'],
			entityType: typeof TYPES.RoleProject | typeof TYPES.User,
			projectId: Project['id']
		) => void
	) {
		this.onClearBookings = callback;
	}

	setProjectColor(color: string) {
		this.projectColor = color;
	}

	setProjectPlannedStartDate(date: Date) {
		this.projectPlannedStartDate = date;
	}

	getProjectPlannedStartDate(
		_item: ResourcePlanningItem | null = null
	): Date {
		return this.projectPlannedStartDate;
	}

	setProjectPlannedEndDate(date: Date | null) {
		this.projectPlannedEndDate = date;
	}

	getProjectPlannedEndDate(
		_item: ResourcePlanningItem | null = null
	): Date | null {
		return this.projectPlannedEndDate;
	}

	setProjectMemberIds(userIds: string[]) {
		this.projectMemberIds = userIds;
	}

	setProjectRetainers(
		retainers: Pick<RetainerPeriod, 'id' | 'startDate' | 'endDate'>[]
	) {
		this.projectRetainerPeriods = retainers;
	}

	getProjectColor(_item: ResourcePlanningItem): string {
		return this.projectColor;
	}

	init(containerId: string) {
		this.applyConfig();

		this.gantt.config.readonly = this.isReadOnly;
		this.gantt.config.columns = this.getColumnConfig();
		this.gantt.config.external_render = {
			isElement: (data: ExternalRendererPayload) => {
				return data.component;
			},
			renderElement: (
				data: ExternalRendererPayload,
				container: HTMLElement
			) => {
				renderComponent(container, data.component, data.props);
			},
		};
		this.gantt.init(containerId);
	}

	addItemToChart(
		item: ResourcePlanningItem,
		parent?: string,
		shouldAddToUndoStack = false,
		index?: number
	) {
		this.shouldAddToUndoStack = shouldAddToUndoStack;
		this.gantt.addTask(item, parent, index);
		this.shouldAddToUndoStack = true;
	}

	updateChartItem(
		id: string,
		item?: ResourcePlanningItem,
		shouldAddToUndoStack = false
	) {
		if (shouldAddToUndoStack) {
			this.gantt.updateTask(id, item);
		} else {
			this.gantt.refreshTask(id);
		}
	}

	deleteItemFromChart(id: string | number, shouldAddToUndoStack = false) {
		this.shouldAddToUndoStack = shouldAddToUndoStack;
		if (this.gantt.isTaskExists(id)) {
			this.gantt.deleteTask(id);
		}
		this.shouldAddToUndoStack = true;
	}

	_isFixedBookingTypeFilter() {
		return this.bookingTypeFilterValue === BOOKING_TYPE_FILTER.FIXED;
	}

	_isTentativeBookingTypeFilter() {
		return this.bookingTypeFilterValue === BOOKING_TYPE_FILTER.TENTATIVE;
	}

	_isFixedAndTentativeBookingTypeFilter() {
		return (
			this.bookingTypeFilterValue ===
			BOOKING_TYPE_FILTER.FIXED_AND_TENTATIVE
		);
	}

	_getBookedDurationBasedOnFilter(
		fixedBookedDuration: number,
		tentativeBookedDuration: number
	) {
		if (this._isFixedBookingTypeFilter()) {
			return fixedBookedDuration;
		}

		if (this._isTentativeBookingTypeFilter()) {
			return tentativeBookedDuration;
		}

		return fixedBookedDuration + tentativeBookedDuration;
	}

	_hideMetricsByFilter(
		utilizationMetricFixed: HTMLDivElement,
		utilizationMetricTentative: HTMLDivElement
	) {
		if (this._isFixedBookingTypeFilter()) {
			utilizationMetricTentative.className =
				'resource-planning__heatmap-utilization-tentative--hidden';
		}

		if (this._isTentativeBookingTypeFilter()) {
			utilizationMetricFixed.className =
				'resource-planning__heatmap-utilization-fixed--hidden';
		}
	}

	_hideProjectLineMetrics(
		utilizationMetricFixed: HTMLDivElement,
		utilizationMetricTentative: HTMLDivElement
	) {
		utilizationMetricFixed.className =
			'resource-planning__heatmap-utilization-fixed--hidden';
		utilizationMetricTentative.className =
			'resource-planning__heatmap-utilization-tentative--hidden';
	}

	_getUtilizedIntensity(utilizedPercentTotal: number) {
		if (isNaN(utilizedPercentTotal)) {
			return 1;
		}

		if (utilizedPercentTotal > 100) {
			return 0.9 - Math.min(utilizedPercentTotal - 100, 200) / 222;
		}

		return 0.8 - utilizedPercentTotal / 125;
	}

	_shouldDisplayTimeOffIcon(
		startDate: Date,
		endDate: Date,
		timeOff: TimeOff,
		utilization: { [key: string]: ResourcingUserUtilization } | undefined
	) {
		if (
			timeOff.type.systemName === TimeOffSystemType.ExtraAvailability ||
			!utilization
		) {
			return false;
		}

		const allDaysInInterval = eachDayOfInterval({
			start: startDate,
			end: addDays(endDate, -1),
		});

		const hasAvailability = allDaysInInterval.some((day) => {
			const dateString = dateToMysqlString(day);
			if (utilization && dateString in utilization) {
				const dateInUtilizationdata = utilization[dateString];

				return dateInUtilizationdata.availability > 0;
			}

			return false;
		});

		return !hasAvailability;
	}

	_setBackgroundIntensityVariable(
		element: HTMLDivElement,
		utilizedIntensity: number
	) {
		element.style.setProperty(
			'--background-intensity',
			String(utilizedIntensity)
		);
	}

	_calculateMetricElementHeight(utilizationPercent: number) {
		return utilizationPercent > 100 ? 100 : utilizationPercent;
	}

	_calculateUtilizedPercentage(bookedDuration: number, availability: number) {
		return Math.round((bookedDuration / availability) * 100);
	}

	_getCachedHeatMapElement(elemId: string) {
		if (this.cachedHeatMapElements.has(elemId)) {
			return this.cachedHeatMapElements
				.get(elemId)
				?.cloneNode(true) as HTMLElement;
		}

		return undefined;
	}

	_makeEmptyHeatMapUtilizationCell(startDate: Date, endDate: Date) {
		const elemWidth = this._getElementWidth(startDate, endDate);
		const heatMapElement = document.createElement('div');

		if (elemWidth === 0) {
			return undefined;
		}

		const elemId = JSON.stringify([startDate, endDate]);
		const cachedHeatMapElement = this._getCachedHeatMapElement(elemId);

		if (cachedHeatMapElement) {
			cachedHeatMapElement.style.width = `${elemWidth}px`;
			return cachedHeatMapElement;
		}

		heatMapElement.className = 'resource-planning__heatmap-element';
		heatMapElement.style.width = `${elemWidth}px`;
		heatMapElement.style.height = `${this.rowHeight}px`;

		return heatMapElement;
	}

	_makeHeatMapUtilizationCell(
		item: ResourcePlanningItem,
		startDate: Date,
		endDate: Date,
		resourceUtilization: BookingsHeatMapData,
		duration?: 'day' | 'month' | 'year'
	) {
		const zoomLevel = this.gantt.ext.zoom.getCurrentLevel();
		const isStartDateWeekend = isWeekend(startDate);
		/**
		 * Heat map id consists of data that makes each element unique
		 * We clone already existing one instead of drawing a new one
		 */
		const elemId = JSON.stringify([
			item.id,
			resourceUtilization,
			duration,
			this.bookingTypeFilterValue,
			zoomLevel,
			isStartDateWeekend,
			this.heatMapDisplayValue,
		]);

		const heatMapElement = this._makeEmptyHeatMapUtilizationCell(
			startDate,
			endDate
		);

		if (!heatMapElement) {
			return;
		}

		const cachedHeatMapElement = this._getCachedHeatMapElement(elemId);

		if (cachedHeatMapElement) {
			cachedHeatMapElement.style.width = `${this._getElementWidth(
				startDate,
				endDate
			)}px`;
			return cachedHeatMapElement;
		}

		const { fixedBookedDuration, tentativeBookedDuration, availability } =
			resourceUtilization;

		const bookedDuration = this._getBookedDurationBasedOnFilter(
			fixedBookedDuration,
			tentativeBookedDuration
		);

		const utilizedPercentTotal = this._calculateUtilizedPercentage(
			bookedDuration,
			availability
		);
		const utilizedPercentFixed = this._calculateUtilizedPercentage(
			fixedBookedDuration,
			availability
		);
		const utilizedPercentTentative = this._calculateUtilizedPercentage(
			tentativeBookedDuration,
			availability
		);

		const fixedMetricElement = document.createElement('div');
		const tentativeMetricElement = document.createElement('div');
		const heatMapTextElement = document.createElement('span');

		fixedMetricElement.className =
			'resource-planning__heatmap-utilization-fixed';
		tentativeMetricElement.className =
			'resource-planning__heatmap-utilization-tentative';
		heatMapTextElement.className = 'resource-planning__heatmap-text';

		this._hideMetricsByFilter(fixedMetricElement, tentativeMetricElement);

		if (
			item.item_type === CHART_ITEM_TYPE.PROJECT ||
			item.item_type === CHART_ITEM_TYPE.ADD_TO_PROJECT ||
			item.item_type === CHART_ITEM_TYPE.BOOKING_LIST
		) {
			this._hideProjectLineMetrics(
				fixedMetricElement,
				tentativeMetricElement
			);
		}

		if (item.item_type === CHART_ITEM_TYPE.ROLE && availability <= 0) {
			if (
				duration === 'day' &&
				!this.includeWeekendsWhenScheduling.value &&
				isStartDateWeekend
			) {
				fixedMetricElement.className +=
					' resource-planning__heatmap-utilization-fixed--unavailable';
				tentativeMetricElement.className +=
					' resource-planning__heatmap-utilization-tentative--unavailable';
				heatMapTextElement.className +=
					' resource-planning__heatmap-text--unavailable';
			} else {
				if (
					!this._isFixedBookingTypeFilter() &&
					tentativeBookedDuration > 0
				) {
					heatMapElement.setAttribute('data-task-id', item.id);
					fixedMetricElement.className +=
						' resource-planning__heatmap-utilization-fixed--overbooked';
					tentativeMetricElement.className +=
						' resource-planning__heatmap-utilization-tentative--overbooked';
				} else {
					fixedMetricElement.className +=
						' resource-planning__heatmap-utilization-fixed--no-capacity';
					tentativeMetricElement.className +=
						' resource-planning__heatmap-utilization-tentative--no-capacity';
					heatMapTextElement.className +=
						' resource-planning__heatmap-text--unavailable';
				}
			}
		} else if (availability <= 0) {
			if (
				item.item_type === CHART_ITEM_TYPE.PROJECT ||
				item.item_type === CHART_ITEM_TYPE.ADD_TO_PROJECT ||
				item.item_type === CHART_ITEM_TYPE.BOOKING_LIST
			) {
				heatMapTextElement.className +=
					' resource-planning__heatmap-text--project-unavailable';
			} else {
				fixedMetricElement.className +=
					' resource-planning__heatmap-utilization-fixed--unavailable';
				tentativeMetricElement.className +=
					' resource-planning__heatmap-utilization-tentative--unavailable';
				heatMapTextElement.className +=
					' resource-planning__heatmap-text--unavailable';
			}
		} else {
			if (utilizedPercentTotal > 0) {
				heatMapElement.setAttribute('data-task-id', item.id);
			} else {
				heatMapElement.className +=
					' resource-planning__heatmap-element--no-modify';
			}
		}

		const utilizedIntensity =
			this._getUtilizedIntensity(utilizedPercentTotal);

		this._setBackgroundIntensityVariable(
			fixedMetricElement,
			utilizedIntensity
		);

		if (utilizedPercentTotal > 100) {
			fixedMetricElement.className +=
				' resource-planning__heatmap-utilization-fixed--overbooked';
			tentativeMetricElement.className +=
				' resource-planning__heatmap-utilization-tentative--overbooked';
			heatMapTextElement.className +=
				' resource-planning__heatmap-text--overbooked';
		}

		const fixedMetricElementHeight =
			this._calculateMetricElementHeight(utilizedPercentFixed);
		const tentativeMetricElementHeight =
			item.item_type === CHART_ITEM_TYPE.ROLE && availability <= 0
				? 100
				: this._calculateMetricElementHeight(utilizedPercentTentative);

		fixedMetricElement.style.height = `${fixedMetricElementHeight}%`;
		tentativeMetricElement.style.height = `${tentativeMetricElementHeight}%`;

		if (this._isFixedAndTentativeBookingTypeFilter()) {
			const remainingSpaceAfterFixedMetric =
				100 - fixedMetricElementHeight;
			tentativeMetricElement.style.height = `${Math.min(
				tentativeMetricElementHeight,
				remainingSpaceAfterFixedMetric
			)}%`;
			tentativeMetricElement.style.top = `${Math.max(
				0,
				remainingSpaceAfterFixedMetric - tentativeMetricElementHeight
			)}%`;
		}

		heatMapTextElement.innerText = this._makeUtilizationDisplayValue(
			item.item_type,
			bookedDuration,
			availability,
			isStartDateWeekend,
			duration
		);

		heatMapElement.appendChild(tentativeMetricElement);
		heatMapElement.appendChild(fixedMetricElement);
		heatMapElement.appendChild(heatMapTextElement);

		this.cachedHeatMapElements.set(elemId, heatMapElement);

		return heatMapElement;
	}

	_makeTimeOffCell(
		item: ResourcePlanningItem,
		startDate: Date,
		endDate: Date,
		timeOff: TimeOff,
		utilization: { [key: string]: ResourcingUserUtilization } | undefined
	) {
		// don't draw the cell when there's no time off or the cell is for a role or booking list row
		if (
			!timeOff?.type ||
			item?.role_id ||
			item.item_type === CHART_ITEM_TYPE.BOOKING_LIST ||
			item.item_type === CHART_ITEM_TYPE.ADD_TO_PROJECT
		) {
			return;
		}

		const timeOffElement = document.createElement('div');
		timeOffElement.className =
			'resource-planning__time-off-element js-time-off-element';

		const startDatePos = this.gantt.posFromDate(startDate);
		const elemWidth = this._getElementWidth(startDate, endDate);
		const tooltipTimeout = String(this.gantt.config.tooltip_timeout);
		if (
			this._shouldDisplayTimeOffIcon(
				startDate,
				endDate,
				timeOff,
				utilization
			)
		) {
			timeOffElement.innerHTML = getIconHTML(
				'icon-timeoff-16',
				Constants.Colors.INACTIVE_HOVER_COLOR
			);
		}

		if (elemWidth <= 0) {
			return;
		}

		timeOffElement.style.width = `${elemWidth}px`;
		timeOffElement.style.height = `${this.rowHeight}px`;
		timeOffElement.style.left = `${startDatePos}px`;

		const timeOffHighlight = document.createElement('div');
		timeOffHighlight.className = 'time-off-highlight';

		timeOffHighlight.classList.add('js-open-time-off');
		timeOffHighlight.classList.add(
			timeOff.type.systemName === TimeOffSystemType.ExtraAvailability
				? 'time-off-highlight--extra'
				: 'time-off-highlight--off'
		);
		timeOffHighlight.title = this.t(
			timeOff.type.systemName === TimeOffSystemType.ExtraAvailability
				? 'project_bookings_extra_availability'
				: 'project_bookings_no_availability'
		);
		timeOffHighlight.setAttribute('data-tipsy-delay-in', tooltipTimeout);

		timeOffElement.setAttribute('data-user-id', getResourceId(item.id));
		timeOffElement.setAttribute(
			'data-start-date',
			dateToMysqlString(resetTime(startDate))
		);
		timeOffElement.setAttribute(
			'data-end-date',
			dateToMysqlString(resetTime(addDays(endDate, -1)))
		);

		timeOffElement.append(timeOffHighlight);

		return timeOffElement;
	}

	_makeUtilizationDisplayValue(
		itemType: string,
		bookedDuration: number,
		availability: number,
		isWeekend = false,
		duration?: 'day' | 'month' | 'year'
	): string {
		const isRole = itemType === CHART_ITEM_TYPE.ROLE;
		if (availability <= 0) {
			if (
				duration === 'day' &&
				isRole &&
				!this.includeWeekendsWhenScheduling.value &&
				isWeekend
			) {
				return this.t('heatmap-not-available');
			}

			return '';
		}

		if (
			itemType === CHART_ITEM_TYPE.PROJECT ||
			itemType === CHART_ITEM_TYPE.ADD_TO_PROJECT ||
			itemType === CHART_ITEM_TYPE.BOOKING_LIST
		) {
			return '';
		}

		if (this.heatMapDisplayValue === HEAT_MAP_OPTIONS.PERCENT) {
			return bookedDuration >= 0
				? `${Math.round((bookedDuration / availability) * 100)}%`
				: '';
		}

		if (this.heatMapDisplayValue === HEAT_MAP_OPTIONS.BOOKED) {
			return `${shortDuration(bookedDuration)[0].value} ${this.t(
				'heatmap-time-booked'
			)}`;
		}

		if (this.heatMapDisplayValue === HEAT_MAP_OPTIONS.REMAINING) {
			if (availability - bookedDuration <= 0) {
				return `${shortDuration(0)[0].value} ${this.t(
					'heatmap-time-left'
				)}`;
			}

			return `${
				shortDuration(availability - bookedDuration)[0].value
			} ${this.t('heatmap-time-left')}`;
		}

		return '';
	}

	clearCachedSubRowHeatMapData(ids: User['id'][]) {
		ids.forEach((id) => {
			const childRowIds = this.gantt.getChildren(`resource-User-${id}`);

			childRowIds.forEach((rowId) => {
				if (this.cachedHeatMapData[rowId]) {
					delete this.cachedHeatMapData[rowId];
				}
			});
		});
	}

	_setCacheHeatMapData(
		id: string,
		date: Date,
		zoomLevelName: string,
		data: BookingsHeatMapData
	) {
		if (!this.cachedHeatMapData[id]) {
			this.cachedHeatMapData[id] = {};
		}

		if (!this.cachedHeatMapData[id][zoomLevelName]) {
			this.cachedHeatMapData[id][zoomLevelName] = {};
		}

		this.cachedHeatMapData[id][zoomLevelName][dateToMysqlString(date)] =
			data;
	}

	_getCacheHeatMapData(
		id: string,
		date: Date,
		zoomLevelName: string
	): BookingsHeatMapData | undefined {
		return this.cachedHeatMapData[id]?.[zoomLevelName]?.[
			dateToMysqlString(date)
		];
	}

	_getUtilizationForAMonth(
		id: string,
		date: Date,
		userData: ResourcingUserUtilization[]
	): BookingsHeatMapData | undefined {
		let heatMapData = this._getCacheHeatMapData(id, date, ZOOM_UNIT.MONTH);

		if (heatMapData) {
			return heatMapData;
		}

		const allDaysInMonthData = userData.filter((day) => {
			// Not using date-fns isSameMonth due to performance
			const isSameYear = day.date.getFullYear() === date.getFullYear();
			const isSameMonth = day.date.getMonth() === date.getMonth();

			return isSameYear && isSameMonth;
		});

		if (allDaysInMonthData.length === 0) {
			return undefined;
		}

		const { fixedBookedDuration, tentativeBookedDuration } =
			this._getBookedDuration(allDaysInMonthData);

		const availability = allDaysInMonthData.reduce((accumulator, day) => {
			return accumulator + day.availability;
		}, 0);

		heatMapData = {
			fixedBookedDuration,
			tentativeBookedDuration,
			availability,
		};

		this._setCacheHeatMapData(id, date, ZOOM_UNIT.MONTH, heatMapData);

		return heatMapData;
	}

	_getUtilizationForAWeek(
		id: string,
		firstDayOfWeek: Date,
		userData: { [key: string]: ResourcingUserUtilization }
	) {
		let heatMapData = this._getCacheHeatMapData(
			id,
			firstDayOfWeek,
			ZOOM_UNIT.WEEK
		);

		if (heatMapData) {
			return heatMapData;
		}

		const daysInWeekData: ResourcingUserUtilization[] = [];

		const allDaysInWeek = eachDayOfInterval({
			start: firstDayOfWeek,
			end: addDays(firstDayOfWeek, 6),
		});

		for (let i = 0; i < allDaysInWeek.length; i++) {
			const index = dateToMysqlString(allDaysInWeek[i]);

			if (userData[index]) {
				daysInWeekData.push(userData[index]);
			}
		}

		if (daysInWeekData.length === 0) {
			return undefined;
		}

		const { fixedBookedDuration, tentativeBookedDuration } =
			this._getBookedDuration(daysInWeekData);

		const availability = daysInWeekData.reduce((accumulator, day) => {
			return accumulator + day.availability;
		}, 0);

		heatMapData = {
			fixedBookedDuration,
			tentativeBookedDuration,
			availability,
		};

		this._setCacheHeatMapData(
			id,
			firstDayOfWeek,
			ZOOM_UNIT.MONTH,
			heatMapData
		);

		return heatMapData;
	}

	_makeMonthElement(
		item: ResourcePlanningItem,
		date: Date,
		userData: ResourcingUserUtilization[]
	) {
		const endDate = startOfMonth(addMonths(date, 1));
		const resourceUtilization = this._getUtilizationForAMonth(
			item.id,
			date,
			userData
		);

		if (!resourceUtilization) {
			return this._makeEmptyHeatMapUtilizationCell(date, endDate);
		}

		return this._makeHeatMapUtilizationCell(
			item,
			date,
			endDate,
			resourceUtilization
		);
	}

	_makeWeekElement(
		item: ResourcePlanningItem,
		date: Date,
		userData: { [key: string]: ResourcingUserUtilization }
	) {
		const endDate = addDays(date, 7);
		const resourceUtilization = this._getUtilizationForAWeek(
			item.id,
			date,
			userData
		);

		if (!resourceUtilization) {
			return this._makeEmptyHeatMapUtilizationCell(date, endDate);
		}

		return this._makeHeatMapUtilizationCell(
			item,
			date,
			endDate,
			resourceUtilization
		);
	}

	_getHeatMapDayElem(
		item: ResourcePlanningItem,
		userUtilization?: ResourcingUserUtilization
	) {
		if (!userUtilization || !userUtilization.date) {
			return undefined;
		}

		const startDate = resetTime(userUtilization.date);
		const endDate = addDays(startDate, 1);
		const resourceUtilization = {
			fixedBookedDuration: userUtilization.fixedBookedDuration,
			tentativeBookedDuration: userUtilization.tentativeBookedDuration,
			availability: userUtilization.availability,
		};

		return this._makeHeatMapUtilizationCell(
			item,
			startDate,
			endDate,
			resourceUtilization,
			'day'
		);
	}

	_getTimeOffDayElem({
		item,
		timeOff,
		date,
		utilization,
	}: {
		item: ResourcePlanningItem;
		timeOff: TimeOff;
		date: string;
		utilization: { [key: string]: ResourcingUserUtilization } | undefined;
	}) {
		const startDate = resetTime(date);
		const endDate = addDays(startDate, 1);
		return this._makeTimeOffCell(
			item,
			startDate,
			endDate,
			timeOff,
			utilization
		);
	}

	_getTimeOffRangeElem({
		item,
		timeOffData,
		date,
		duration = 'week',
		utilization,
	}: {
		item: ResourcePlanningItem;
		timeOffData: {
			[key: SAPIDate]: TimeOff;
		};
		date: Date;
		duration: 'month' | 'week';
		utilization: { [key: string]: ResourcingUserUtilization };
	}) {
		const startingDate = date;
		const endingDate =
			duration === 'week'
				? addDays(startingDate, 7)
				: startOfMonth(addMonths(startingDate, 1));
		const timeOff = this._getTimeOffWithinDateRange(
			timeOffData,
			startingDate,
			endingDate
		);

		if (timeOff) {
			return this._makeTimeOffCell(
				item,
				startingDate,
				endingDate,
				timeOff,
				utilization
			);
		}

		return undefined;
	}

	_getBookedDuration(userData: ResourcingUserUtilization[]) {
		const fixedBookedDuration = userData.reduce(
			(accumulator, day) => accumulator + day.fixedBookedDuration,
			0
		);

		const tentativeBookedDuration = userData.reduce(
			(accumulator, day) => accumulator + day.tentativeBookedDuration,
			0
		);

		return { fixedBookedDuration, tentativeBookedDuration };
	}

	_getActionsColumnTemplate() {
		let html = '';

		if (this.isReadOnly) {
			return '';
		} else if (this.dragInProgress) {
			const props: ResourcePlanningActionShortcutProps = {
				options: [],
				projectId: '',
				retainerPeriodId: this.retainerPeriodId,
				projectStartDate: resetTime(this.projectStartDate),
				entityId: '',
				entityType: TYPES.User,
				hasTentativeBookingsFeature:
					this.hasTentativeBookingsFeature.value,
				config: {} as this,
				resource: {} as ResourcePlanningItem,
				project: {} as ResourcePlanningItem,
			};

			const utils = renderComponent(
				null,
				'ResourcePlanningActionShortcut',
				props
			);

			html = utils?._container?.innerHTML ?? '';
		}

		return `<div class="resource-planning__actions-shortcut">${html}</div>`;
	}

	getScaleConfigByZoomLevel(zoomLevel: number) {
		const zoomLevelName = ZOOM_SCALE_OPTIONS[zoomLevel];
		const maxZoomLevel = ZOOM_SCALE_OPTIONS.length - 1;
		const config = this._getScaleConfigs().find(
			(row) => row.zoom_level_name === zoomLevelName
		);
		const dayWidthMultiplier = maxZoomLevel - zoomLevel;

		if (config) {
			const minColumnWidth =
				150 * Math.pow(0.66, dayWidthMultiplier) * config.daysInOneUnit;

			config.min_column_width = Math.round(minColumnWidth);
		}

		return config;
	}

	_getScaleConfigs(): ZoomScaleConfig[] {
		return [
			{
				zoom_level_name: ZOOM_OPTIONS.MONTH,
				zoom_level: ZOOM_UNIT.MONTH,
				step: 1,
				unit: ZOOM_UNIT.MONTH,
				scale_unit: ZOOM_UNIT.YEAR,
				date_scale: '%Y',
				daysInOneUnit: 30,
				subscales: [
					{
						unit: ZOOM_UNIT.MONTH,
						step: 1,
						date: '%M',
					},
				],
			},
			{
				zoom_level_name: ZOOM_OPTIONS.WEEK,
				zoom_level: ZOOM_UNIT.WEEK,
				step: 1,
				unit: ZOOM_UNIT.WEEK,
				scale_unit: ZOOM_UNIT.MONTH,
				date_scale: '%F',
				daysInOneUnit: 7,
				subscales: [
					{
						unit: ZOOM_UNIT.WEEK,
						step: 1,
						date: '%W',
					},
				],
			},
			{
				zoom_level_name: ZOOM_OPTIONS.DAY,
				zoom_level: ZOOM_UNIT.DAY,
				step: 1,
				unit: ZOOM_UNIT.DAY,
				scale_unit: ZOOM_UNIT.MONTH,
				date_scale: '%F',
				daysInOneUnit: 1,
				subscales: [
					{
						unit: ZOOM_UNIT.DAY,
						step: 1,
						date: '%d %D',
						css: (date: Date) => {
							if (
								date.getTime() ===
								resetTime(new Date()).getTime()
							) {
								return 'today-marker';
							}
						},
					},
				],
			},
		];
	}

	getAllScaleConfigLevels(): ZoomScaleConfig[] {
		return ZOOM_SCALE_OPTIONS.map((_, i) =>
			this.getScaleConfigByZoomLevel(i)
		).filter(isTruthy);
	}

	_getGridFolderTemplate(item: ResourcePlanningItem): string {
		const options: LiterallyAnything = {};
		let needsReactivity = false;
		let componentName: ResourcePlanningComponent | undefined;

		if (item.item_type === CHART_ITEM_TYPE.RESOURCE) {
			needsReactivity = true;

			componentName = 'ResourcePlanningAvatar';
			options.url = item?.user_avatar ?? '';
			options.initials = item.initials ?? '';
			options.color = item.color ?? PLACEHOLDER_BG_COLOR;
		} else if (item.item_type === CHART_ITEM_TYPE.ROLE) {
			needsReactivity = true;

			componentName = 'ResourcePlanningPlaceholderIcon';

			const containerElementId = this.containerElem?.id;

			if (containerElementId === RESOURCE_PLANNING_CONTAINER_ID) {
				options.iconName = 'icon-addperson-16';
				options.isInteractive = true;
			}

			if (
				containerElementId === RESOURCE_PLANNING_PORTFOLIO_CONTAINER_ID
			) {
				options.iconName = 'icon-person-16';
			}
		} else if (item.item_type === CHART_ITEM_TYPE.PROJECT) {
			componentName = 'ResourcePlanningIcon';
			options.name = item.project_icon_name;
			options.fill = item.color;
		} else if (item.item_type === CHART_ITEM_TYPE.ADD_TO_PROJECT) {
			componentName = 'ResourcePlanningIcon';
			options.name = 'icon-add-16';
		}

		if (!componentName) {
			return '';
		}

		const id = `grid-folder-${getUniqueId()}`;
		const utils = renderComponent(null, componentName, options);

		if (needsReactivity) {
			setTimeout(() => {
				if (componentName) {
					renderComponent(`#${id}`, componentName, options);
				}
			});
		}

		return `<div id="${id}" class="resource-planning__grid-folder">${utils?._container?.innerHTML}</div>`;
	}

	_isBookingOverlapping(item: ResourcePlanningItem): boolean {
		let isBookingOverlapping = false;

		this.gantt.eachTask((target: ResourcePlanningItem) => {
			if (isBookingOverlapping) {
				return;
			}

			if (
				item.id !== target.id &&
				target.item_type === CHART_ITEM_TYPE.BOOKING
			) {
				isBookingOverlapping = areBookingDatesOverlapping(item, target);
			}
		}, item[RESOURCE_PROPERTY]);

		return isBookingOverlapping;
	}

	_isTentativeBooking(item: ResourcePlanningItem): boolean {
		return Boolean(item.is_tentative);
	}

	_hasBookings(item: ResourcePlanningItem): boolean {
		if (item.row_type === CHART_ROW_TYPE.BOOKINGS) {
			const bookings = this.gantt.getTaskBy(
				RESOURCE_PROPERTY,
				item.group_id
			);
			return bookings.length > 0;
		}

		return false;
	}

	_getTaskClass(item: ResourcePlanningItem): string {
		if (item.row_type === CHART_ROW_TYPE.HEATMAP) {
			return 'resource-planning__resource-row';
		}

		switch (item.item_type) {
			case CHART_ITEM_TYPE.BOOKING:
				const rootClass = this._isTentativeBooking(item)
					? 'resource-planning__booking-tentative'
					: 'resource-planning__booking-fixed';

				const classList = [rootClass];

				if (this._isBookingOverlapping(item)) {
					classList.push(`${rootClass}--overlapping`);
				}

				if (!this.isReadOnly) {
					classList.push(`${rootClass}--can-modify`);
				}

				return classList.join(' ');
			case CHART_ITEM_TYPE.PLACEHOLDER:
				return 'resource-planning__placeholder-row';
			case CHART_ITEM_TYPE.BOOKING_LIST:
				return 'resource-planning__booking-row';
			default:
				return '';
		}
	}

	_getGridRowClass(item: ResourcePlanningItem): string {
		if (item.item_type === CHART_ITEM_TYPE.BOOKING_LIST) {
			return 'resource-planning__booking-list-grid-row';
		} else if (item.item_type === CHART_ITEM_TYPE.ADD_TO_PROJECT) {
			return 'resource-planning__add-to-project';
		}

		return '';
	}

	_getTaskRowClass(item: ResourcePlanningItem): string {
		if (
			item.item_type === CHART_ITEM_TYPE.PROJECT ||
			item.item_type === CHART_ITEM_TYPE.ADD_TO_PROJECT ||
			item.item_type === CHART_ITEM_TYPE.BOOKING_LIST
		) {
			return 'resource-planning__project-task-row';
		}

		return '';
	}

	_getTaskText(item: ResourcePlanningItem): string {
		if (
			item.item_type === CHART_ITEM_TYPE.RESOURCE ||
			item.item_type === CHART_ITEM_TYPE.BOOKING_LIST
		) {
			return '';
		}

		if (item.item_type === CHART_ITEM_TYPE.BOOKING && item.description) {
			const safeDescription = sanitizeHTML(item.description);

			return `<div class="resource-planning__booking-text-container"><span class="resource-planning__booking-description">${safeDescription}</span> <span class="resource-planning__booking-hours">(${item.text})</span></div>`;
		}

		return item.text;
	}

	_getGridOpenTemplate(item: ResourcePlanningItem): string {
		let iconName = '';

		if (item.item_type === CHART_ITEM_TYPE.PROJECT) {
			iconName = 'icon-expanddown-16';
		} else if (
			item.item_type === CHART_ITEM_TYPE.RESOURCE ||
			item.item_type === CHART_ITEM_TYPE.ROLE ||
			item.item_type === CHART_ITEM_TYPE.ROLE_DIRECTORY
		) {
			iconName = item.$open
				? 'icon-arrow-down-16'
				: 'icon-arrow-right-16';
		} else {
			return '';
		}

		const utils = renderComponent(null, 'ResourcePlanningIcon', {
			name: iconName,
		});

		return `<div class="gantt_tree_icon gantt_${
			item.$open ? 'close' : 'open'
		}">${utils?._container?.innerHTML}</div>`;
	}

	_getNameColumnLabel(): string {
		if (this.isReadOnly || !this.isSingleProject) {
			return this.t('resource_planning_name_column_title');
		}

		const utils = renderComponent(null, 'ResourcePlanningAddMember', {
			iconName: 'icon-add-16',
			text: this.t('resource_planning_name_column_title'),
		});

		return `<div class="js-add-project-member" data-placement="bottom-start">${
			utils?._container?.innerHTML ?? ''
		}</div>`;
	}

	_getBookedDurationColumnLabel(): string {
		return `<div><span title="${this.t(
			'resource_planning_booked_duration_tooltip'
		)}">${this.t('resource_planning_booked_duration')}</span></div>`;
	}

	_getNameColumnTemplate(item: ResourcePlanningItem): string {
		const isBookingsTypeForSingleProject =
			this.isSingleProject &&
			item.item_type === CHART_ITEM_TYPE.BOOKING_LIST;

		const isProjectTypeForBookingsModule =
			!this.isSingleProject && item.item_type === CHART_ITEM_TYPE.PROJECT;

		if (isBookingsTypeForSingleProject || isProjectTypeForBookingsModule) {
			let isBookingOverlapping = false;

			this.gantt.eachTask((chartItem) => {
				if (isBookingOverlapping) {
					return;
				}

				isBookingOverlapping = this._isBookingOverlapping(chartItem);
			}, item.group_id);

			if (isBookingOverlapping) {
				const id = `overlapping-bookings-${getUniqueId()}`;

				setTimeout(() => {
					renderComponent(`#${id}`, 'ResourcePlanningIcon', {
						name: 'icon-alert-16',
						text: item.text,
						tooltip: this.t(
							'resource_planning_overlapping_bookings'
						),
						isLink: !this.isSingleProject,
					});
				});

				return `<div id="${id}" class="resource-planning__overlapping-bookings"></div>`;
			} else if (!this.isSingleProject) {
				return `<a class="resource-planning__project-link" data-tipsy-delay-in="300" title="${item.text}">${item.text}</a>`;
			} else {
				return item.text;
			}
		} else if (
			item.item_type === CHART_ITEM_TYPE.PLACEHOLDER ||
			item.item_type === CHART_ITEM_TYPE.ADD_TO_PROJECT
		) {
			return item.text;
		}

		return `<div class="resource-planning__name"><span data-tipsy-delay-in="300" title="${item.role_name ?? item.text}">${item.text}</span></div>`;
	}

	_getBookedDurationColumnTemplate(item: ResourcePlanningItem): string {
		if (
			CHART_ITEM_TYPE.PROJECT === item.item_type ||
			CHART_ITEM_TYPE.BOOKING_LIST === item.item_type
		) {
			let duration = 0;
			const parent =
				CHART_ITEM_TYPE.PROJECT === item.item_type
					? this.chartProjectId
					: item.id;

			this.gantt.eachTask((item) => {
				if (
					item.item_type !== CHART_ITEM_TYPE.BOOKING ||
					!item.start_date ||
					!item.end_date
				) {
					return;
				}

				const resourceId = getResourceId(item[RESOURCE_PROPERTY]);
				const availability =
					this.availabilityService.getUserAvailability(resourceId);

				duration += getTotalBookingDuration({
					startDate: item.start_date,
					// Adjust the end date by subtracting one day to correct for an extra day added in _getBookingEndDate
					endDate: addDays(item.end_date, -1),
					bookingType: item.booking_type,
					bookedDuration: item.booked_duration ?? 0,
					bookedPercent: item.booked_duration ?? 0,
					userAvailability: availability,
					resourceType: getResourceType(item[RESOURCE_PROPERTY]),
					resourceId: resourceId,
					includeWeekends: this.includeWeekendsWhenScheduling.value,
				}).duration;
			}, parent);

			return this.formatter.formatLongDuration({
				duration,
				removeMinutesOnFullHours: true,
			});
		}

		return '';
	}

	getProjectId(): string {
		return String(this.chartProjectId.split('-').pop());
	}

	getQuickActionProjectId(_item: ResourcePlanningItem) {
		return this.getProjectId();
	}

	getQuickActionEntityId(item: ResourcePlanningItem) {
		return getResourceId(item.id);
	}

	getQuickActionEntityType(item: ResourcePlanningItem) {
		const entityType = getResourceType(item.id);

		return entityType === TYPES.User ? TYPES.User : TYPES.RoleProject;
	}

	getQuickActionTargetProject(_item: ResourcePlanningItem) {
		return this.gantt.getTask(this.chartProjectId);
	}

	getQuickActionTargetResource(item: ResourcePlanningItem) {
		return this.gantt.getTask(item.group_id ?? '');
	}

	onBookingClick(id: string, e: MouseEvent) {
		const item = this.gantt.getTask(id);

		if (!item || item.item_type !== CHART_ITEM_TYPE.BOOKING) {
			return;
		}

		const zoomLevel = Number(
			getResourcePlanningSetting(
				this.getProjectId(),
				SETTINGS.ZOOM_LEVEL
			) ?? 0
		);
		const zoomLevelName = ZOOM_SCALE_OPTIONS[zoomLevel];
		const selectedEntityType =
			getResourceType(item.resource_id) === TYPES.User
				? TYPES.User
				: TYPES.RoleProject;

		const target = document.createElement('div');
		target.style.position = 'fixed';
		target.style.left = `${e.clientX}px`;
		target.style.top = `${e.clientY}px`;

		document.body.append(target);

		const props: ResourcePlanningBookingModalProps & {
			onUpdateBooking: (
				input: BookingSaveInput,
				oldEntityId: User['id'] | RoleProject['id'],
				oldEntityType: typeof TYPES.RoleProject | typeof TYPES.User
			) => void;
		} = {
			bookingId: item.id,
			startDate: item.start_date,
			// Adjust the end date by subtracting one day to correct for an extra day added in _getBookingEndDate
			endDate: addDays(item.end_date, -1),
			zoomLevel: zoomLevelName,
			projectStartDate: resetTime(this.projectStartDate),
			projectId: this.getProjectId(),
			selectedEntityId: getResourceId(item.resource_id),
			selectedEntityType,
			amount: item.booked_duration,
			amountType: item.booking_type,
			isTentative: item.is_tentative,
			description: item.description,
			hasTentativeBookingsFeature: this.hasTentativeBookingsFeature.value,
			onUpdateBooking: (
				input: BookingSaveInput,
				oldEntityId: User['id'] | RoleProject['id'],
				oldEntityType: typeof TYPES.RoleProject | typeof TYPES.User
			) => {
				this.onUpdateBooking?.(input, oldEntityId, oldEntityType);
			},
		};

		openBookingModal(target, props, {
			placement: 'top',
		});
	}

	getParentResourceId(
		booking: Booking | ResourcePlanningQuery['BookingsList'][number]
	) {
		if (booking.roleProject) {
			return getChartResourceId(
				booking.roleProject?.id,
				TYPES.RoleProject
			);
		} else {
			return getChartResourceId(booking.user?.id ?? '');
		}
	}

	bookingToChartItem(
		booking: Booking | ResourcePlanningQuery['BookingsList'][number]
	): ResourcePlanningItem {
		const duration = this.formatter.formatLongDuration({
			duration: booking.amount,
			removeMinutesOnFullHours: true,
		});

		let text = '';
		if (booking.amountType === BookingAmountType.Hour) {
			text = `${duration} / ${this.t('day')}`;
		} else if (booking.amountType === BookingAmountType.Percent) {
			text = `${booking.amount}% / ${this.t('day')}`;
		} else {
			text = duration;
		}
		const parent = this.getParentResourceId(booking);

		return {
			id: String(booking.id),
			text,
			item_type: CHART_ITEM_TYPE.BOOKING,
			booked_duration: booking.amount,
			booking_type: booking.amountType,
			start_date: resetTime(booking.startDate),
			end_date: this._getBookingEndDate(booking.endDate),
			is_tentative: booking.isTentative,
			description: booking.description,
			[RESOURCE_PROPERTY]: parent,
			parent,
			render: RENDER_TYPE_SPLIT,
		};
	}

	_getBookingEndDate(date: string): Date {
		const tmpDate = resetTime(date);

		return addDays(tmpDate, 1);
	}

	_getScaleCellClass(): string {
		return 'month-scale js-resource-planning-month-scale';
	}

	_getTaskTextByPosition(
		item: ResourcePlanningItem,
		position: 'left' | 'right'
	) {
		const state = this.gantt.getState();
		const modes = this.gantt.config.drag_mode;
		const isRightText = position === 'right';

		if (
			state.drag_id === item.id &&
			[modes.move, modes.resize].includes(state.drag_mode)
		) {
			if (!item.start_date || !item.end_date) {
				return '';
			}

			if (state.drag_mode === modes.resize) {
				if (state.drag_from_start) {
					return !isRightText
						? this.formatter.formatDateNoCurrentYear(
								item.start_date
							)
						: '';
				}

				return isRightText
					? this.formatter.formatDateNoCurrentYear(item.end_date)
					: '';
			}

			return this.formatter.formatDateNoCurrentYear(
				isRightText ? item.end_date : item.start_date
			);
		}

		return '';
	}

	_getTooltipText(item: ResourcePlanningItem) {
		if (this.dragInProgress) {
			return '';
		}

		if (item.item_type === CHART_ITEM_TYPE.BOOKING) {
			return item.text;
		}

		return '';
	}

	getZoomLevelSetting = () => {
		let zoomLevel = getResourcePlanningSetting(
			this.getProjectId(),
			SETTINGS.ZOOM_LEVEL
		);

		if (zoomLevel === undefined) {
			zoomLevel = this.defaultZoomLevel ?? getZoomToFitZoomLevel(this);
		}

		return parseInt(`${zoomLevel}`);
	};

	setChartMinMaxDate() {
		let { minDate, maxDate } = this.getChartMinMaxDate();
		const prevMinDate: Date | undefined = this.gantt.config.start_date;
		const prevMaxDate: Date | undefined = this.gantt.config.end_date;

		const { x, y } = this.gantt.getScrollState();
		const dateToScrollTo = this.gantt.dateFromPos(x);
		const zoomConfig = this.getScaleConfigByZoomLevel(
			this.getZoomLevelSetting()
		);

		switch (zoomConfig?.unit) {
			case ZOOM_UNIT.MONTH:
				minDate = addMonths(minDate, -1);
				maxDate = addMonths(maxDate, 1);
				break;
			case ZOOM_UNIT.WEEK:
				minDate = addDays(minDate, -21);
				maxDate = addDays(maxDate, 21);
				break;
			default:
				minDate = addDays(minDate, -7);
				maxDate = addDays(maxDate, 7);
				break;
		}

		if (
			!prevMinDate ||
			!prevMaxDate ||
			isBefore(minDate, prevMinDate) ||
			isAfter(maxDate, prevMaxDate)
		) {
			this.gantt.config.start_date = minDate;
			this.gantt.config.end_date = maxDate;
			this.gantt.render();

			this.gantt.scrollTo(this.gantt.posFromDate(dateToScrollTo), y);
		}
	}

	getChartMinMaxDate() {
		const dates: Date[] = [
			this.getProjectPlannedStartDate(),
			this.getProjectPlannedEndDate() ??
				this.getProjectPlannedStartDate(),
		];

		this.gantt.eachTask((item) => {
			if (
				item.item_type === CHART_ITEM_TYPE.BOOKING &&
				item.start_date &&
				item.end_date
			) {
				dates.push(item.start_date, item.end_date);
			}
		});

		const minDate = resetTime(min(dates));
		const maxDate = resetTime(max(dates));

		return { minDate, maxDate };
	}

	groupBy(resources: ResourcePlanningItem[]) {
		const { x, y } = this.gantt.getScrollState();
		const dateToScrollTo = this.gantt.dateFromPos(x);

		this.gantt.groupBy({
			groups: resources,
			save_tree_structure: true,
			relation_property: this.gantt.config.resource_property,
			group_id: 'group_id',
			group_text: 'text',
		});

		this.gantt.scrollTo(this.gantt.posFromDate(dateToScrollTo), y);

		// TODO: figure out sorting
		// this.gantt.sort('id');
	}

	clearChart() {
		this.gantt.clearAll();
	}

	refreshData() {
		this.gantt.refreshData();
	}

	refreshDataWithoutFocus() {
		// Guard against missing gantt (for edge cases)
		if (!this.gantt) {
			return;
		}

		// Temporarily disable keyboard navigation which causes the gantt to scroll back to the focused element after data changes
		this.gantt.config.keyboard_navigation = false;
		this.gantt.refreshData();
		this.gantt.config.keyboard_navigation = true;
	}

	getMoveBookingsConfirmationLangVar() {
		return 'resource_booking_replace_member_confirm';
	}

	getClearBookingsConfirmationLangVar() {
		return 'resource_booking_clear_bookings_confirm';
	}

	getRemoveProjectMemberConfirmationLangVar() {
		return 'resource_booking_delete_member_confirm';
	}

	_getTimeOffWithinDateRange(
		timeOffData: { [key: SAPIDate]: TimeOff },
		start: Date,
		end: Date
	): TimeOff | undefined {
		let timeOff = undefined;
		const keys = Object.keys(timeOffData);

		for (const key of keys) {
			// Showing vacation within the range is higher priority.
			// If we already found a vacation type, we don't need to iterate further.
			if (timeOff?.type?.systemName === TimeOffSystemType.Vacation) {
				break;
			}

			if (
				isWithinInterval(new Date(key), {
					start,
					end,
				})
			) {
				timeOff = {
					...timeOffData[key],
				};
			}
		}

		return timeOff;
	}

	_getElementWidth(startDate: Date, endDate: Date): number {
		return (
			this.gantt.posFromDate(endDate) - this.gantt.posFromDate(startDate)
		);
	}

	_getDaysWithinRange(start: Date, end: Date): SAPIDate[] {
		const dates = eachDayOfInterval({ start, end });

		return dates.map((date) => dateToMysqlString(date));
	}

	clearCachedHeatMapElements() {
		this.cachedHeatMapElements.clear();
	}

	addTodaysMarker() {
		this.gantt.addMarker({
			start_date: new Date(),
			css: 'today',
			title: this.t('today'),
		});
	}

	attachWeekDatesTooltip() {
		this.gantt.ext.tooltips.tooltipFor({
			selector: '.gantt_scale_cell',
			html: (event: MouseEvent, node: HTMLElement) => {
				const zoomConfig = this.getScaleConfigByZoomLevel(
					this.getZoomLevelSetting()
				);

				if (
					zoomConfig?.zoom_level === ZOOM_UNIT.WEEK &&
					!node.closest('.js-resource-planning-month-scale')
				) {
					const relativePosition =
						this.gantt.utils.dom.getRelativeEventPosition(
							event,
							this.gantt.$task_scale
						);
					const date = this.gantt.dateFromPos(relativePosition.x);
					const weekStartsOn = this.weekStartsOn.value;

					return `${this.formatter.formatDate(
						startOfWeek(date, {
							weekStartsOn,
						})
					)} - ${this.formatter.formatDate(
						endOfWeek(date, {
							weekStartsOn,
						})
					)}`;
				}

				return '';
			},
		});
	}
}
